import React, { useEffect, useState } from 'react';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getCountryFlag } from '../utils/countries';
import { POWER_CATEGORIES } from '../constants/powerGroups';
import PodiumFireworks from './PodiumFireworks';
import MarqueeText from './MarqueeText';
import LoadMoreSentinel, { LIST_CHUNK_SIZE } from './LoadMoreSentinel';
import './ResultsPodium.css';

const AGE_GROUPS = ['<19', '19-34', '35-39', '40-44', '45-49', '50-54', '55-59', '60-64', '65-69', '70-74', '75-79', '80+'];
const POWER_GROUPS = POWER_CATEGORIES.map(c => c.cat);
const GENDER_GROUPS = ['male', 'female'];

const ResultsPodium = ({ results, calculateAgeGroup, calculatePowerGroup, formatTime, language, t }) => {
  const [rankBy, setRankBy] = useState('time'); // 'time' | 'points'
  const [gender, setGender] = useState(null); // 'male' | 'female'
  const [extraDimension, setExtraDimension] = useState(null); // null | 'power' | 'age'
  const [extraValue, setExtraValue] = useState(null);
  // Список под подиумом рисуется порциями, как таблицы протокола
  const [restCount, setRestCount] = useState(LIST_CHUNK_SIZE);

  const riderName = (result) => {
    if (language !== 'ru' && result.first_name_eng && result.last_name_eng) {
      return `${result.first_name_eng} ${result.last_name_eng}`;
    }
    return `${result.first_name} ${result.last_name}`;
  };

  const eligibleForRanking = (result) => {
    return rankBy === 'points' ? result.total_points > 0 : result.total_time !== null;
  };

  const sortForRanking = (list) => {
    const sorted = [...list];
    if (rankBy === 'points') {
      sorted.sort((a, b) => (b.total_points || 0) - (a.total_points || 0));
    } else {
      sorted.sort((a, b) => {
        const stagesDiff = (b.routes_completed || 0) - (a.routes_completed || 0);
        if (stagesDiff !== 0) return stagesDiff;
        return a.total_time - b.total_time;
      });
    }
    return sorted;
  };

  const renderSlots = (top3) => {
    const slots = [top3[0] || null, top3[1] || null, top3[2] || null];

    return (
      <div className="podium-stand">
        {slots.map((result, index) => {
          const place = index + 1;
          return (
            <div key={place} className={`podium-slot podium-slot-${place} ${!result ? 'podium-slot-empty' : ''}`}>
              {result && (
                <>
                  {result.country && getCountryFlag(result.country) && (
                    <div className="podium-flag">{getCountryFlag(result.country)}</div>
                  )}
                  <div className="podium-avatar-wrapper">
                    <img
                      src={getUserAvatarUrl(result.avatar)}
                      onError={handleUserAvatarError}
                      alt={riderName(result)}
                      className="podium-avatar"
                    />
                  </div>
                  <MarqueeText text={riderName(result)} className="podium-name" />
                  <div className="podium-value">
                    {rankBy === 'points' ? result.total_points : formatTime(result.total_time)}
                  </div>
                </>
              )}
              <div className={`podium-bar podium-bar-${place}`}>
                <span className="podium-bar-place">{place}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRestList = (rest) => (
    <>
      <div className="podium-rest-list">
        {rest.slice(0, restCount).map((result, index) => (
          <div key={result.user_id} className="podium-rest-row">
            <span className="podium-rest-place">{index + 4}</span>
            {result.country && getCountryFlag(result.country) && (
              <span className="podium-rest-flag">{getCountryFlag(result.country)}</span>
            )}
            <MarqueeText text={riderName(result)} className="podium-rest-name" />
            <span className="podium-rest-value">
              {rankBy === 'points' ? result.total_points : formatTime(result.total_time)}
            </span>
          </div>
        ))}
      </div>
      {restCount < rest.length && (
        <LoadMoreSentinel
          key={restCount}
          onVisible={() => setRestCount(prev => Math.max(prev, restCount + LIST_CHUNK_SIZE))}
        />
      )}
    </>
  );

  const availableGenders = GENDER_GROUPS.filter(g => results.some(r => r.gender === g));
  const activeGender = (gender && availableGenders.includes(gender)) ? gender : availableGenders[0] || null;

  const genderFiltered = activeGender ? results.filter(r => r.gender === activeGender) : results;

  const availablePowerGroups = POWER_GROUPS.filter(g => genderFiltered.some(r => calculatePowerGroup(r.power_group) === g));
  const availableAgeGroups = AGE_GROUPS.filter(g => genderFiltered.some(r => calculateAgeGroup(r.birth_date) === g));

  const handleGenderSelect = (g) => {
    setGender(g);
    setExtraDimension(null);
    setExtraValue(null);
  };

  const handleDimensionSelect = (dimension) => {
    setExtraDimension(dimension);
    if (dimension === 'power') {
      setExtraValue(availablePowerGroups[0] || null);
    } else if (dimension === 'age') {
      setExtraValue(availableAgeGroups[0] || null);
    } else {
      setExtraValue(null);
    }
  };

  let finalList = genderFiltered;
  if (extraDimension === 'power' && extraValue) {
    finalList = genderFiltered.filter(r => calculatePowerGroup(r.power_group) === extraValue);
  } else if (extraDimension === 'age' && extraValue) {
    finalList = genderFiltered.filter(r => calculateAgeGroup(r.birth_date) === extraValue);
  }

  const waitingForValue = extraDimension && !extraValue;
  const eligibleSorted = waitingForValue ? [] : sortForRanking(finalList.filter(eligibleForRanking));
  const top3 = eligibleSorted.slice(0, 3);
  const rest = eligibleSorted.slice(3);
  const hasPodium = top3.length > 0;

  const breadcrumb = [
    t(rankBy === 'points' ? 'results.summary_rank_points' : 'results.summary_rank_time'),
    activeGender ? t(`results.${activeGender}`) : null,
    extraDimension === 'power' ? t('results.summary_by_power') : extraDimension === 'age' ? t('results.summary_by_age') : null,
    extraDimension && extraValue ? extraValue : null
  ].filter(Boolean).join(' · ');

  const celebrationKey = hasPodium ? `${rankBy}|${breadcrumb}|${top3[0].user_id}` : null;

  // Другой срез итогов — снова первая порция. results сюда не берем: родитель
  // собирает массив заново на каждый рендер, а смена события и так пересоздает подиум
  useEffect(() => {
    setRestCount(LIST_CHUNK_SIZE);
  }, [breadcrumb]);

  return (
    <div className="results-podium">
      <div className="podium-filters">
        <div className="podium-filter-groups">
          <div className="podium-rank-toggle">
            <button
              className={`podium-rank-btn ${rankBy === 'time' ? 'active' : ''}`}
              onClick={() => setRankBy('time')}
            >
              {t('results.summary_rank_time')}
            </button>
            <button
              className={`podium-rank-btn ${rankBy === 'points' ? 'active' : ''}`}
              onClick={() => setRankBy('points')}
            >
              {t('results.summary_rank_points')}
            </button>
          </div>

          {availableGenders.length > 0 && (
            <div className="podium-rank-toggle">
              {availableGenders.map(g => (
                <button
                  key={g}
                  className={`podium-rank-btn ${activeGender === g ? 'active' : ''}`}
                  onClick={() => handleGenderSelect(g)}
                >
                  {t(`results.${g}`)}
                </button>
              ))}
            </div>
          )}

          <div className="podium-rank-toggle">
            <button
              className={`podium-rank-btn ${!extraDimension ? 'active' : ''}`}
              onClick={() => handleDimensionSelect(null)}
            >
              {t('results.summary_dimension_none')}
            </button>
            <button
              className={`podium-rank-btn ${extraDimension === 'power' ? 'active' : ''}`}
              onClick={() => handleDimensionSelect('power')}
            >
              {t('results.summary_by_power')}
            </button>
            <button
              className={`podium-rank-btn ${extraDimension === 'age' ? 'active' : ''}`}
              onClick={() => handleDimensionSelect('age')}
            >
              {t('results.summary_by_age')}
            </button>
          </div>
        </div>

        {extraDimension === 'power' && (
          <div className="podium-value-row">
            {availablePowerGroups.map(g => (
              <button
                key={g}
                className={`podium-value-chip ${extraValue === g ? 'active' : ''}`}
                onClick={() => setExtraValue(g)}
              >
                {g}
              </button>
            ))}
          </div>
        )}

        {extraDimension === 'age' && (
          <div className="podium-value-row">
            {availableAgeGroups.map(g => (
              <button
                key={g}
                className={`podium-value-chip ${extraValue === g ? 'active' : ''}`}
                onClick={() => setExtraValue(g)}
              >
                {g}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="podium-result">
        {breadcrumb && <div className="podium-selection-title">{breadcrumb}</div>}
        {waitingForValue ? (
          <div className="no-results">{t('results.summary_select_value')}</div>
        ) : hasPodium ? (
          <>
            <PodiumFireworks triggerKey={celebrationKey}>
              {renderSlots(top3)}
            </PodiumFireworks>
            {rest.length > 0 && renderRestList(rest)}
          </>
        ) : (
          <div className="no-results">{t('results.summary_no_group_data')}</div>
        )}
      </div>
    </div>
  );
};

export default ResultsPodium;
